/**
 * Enterprise Permission System - Production Grade
 * RBAC + ABAC + Separation of Duties
 * 
 * This is the core permission engine for the Disclosure Operating System.
 */

// =============================================================================
// SESSION STATE (Using var to avoid TDZ errors during module initialization)
// =============================================================================

var currentSession = null;
var snapshotLockState = {
  isLocked: false,
  lockedBy: null,
  lockedAt: null,
  snapshotId: null
};

// =============================================================================
// CONSTANTS
// =============================================================================

export const ROLES = {
  BOARD_VIEWER: 'BOARD_VIEWER',
  EXEC_SPONSOR: 'EXEC_SPONSOR',
  COMPLIANCE_OWNER: 'COMPLIANCE_OWNER',
  COMPLIANCE_ANALYST: 'COMPLIANCE_ANALYST',
  LEGAL_COUNSEL: 'LEGAL_COUNSEL',
  LEGAL_ADMIN: 'LEGAL_ADMIN',
  SUSTAINABILITY_LEAD: 'SUSTAINABILITY_LEAD',
  SUSTAINABILITY_ANALYST: 'SUSTAINABILITY_ANALYST',
  PROCUREMENT_OWNER: 'PROCUREMENT_OWNER',
  REMEDIATION_OWNER: 'REMEDIATION_OWNER',
  INTERNAL_AUDIT: 'INTERNAL_AUDIT',
  EXTERNAL_AUDITOR: 'EXTERNAL_AUDITOR',
  SYSTEM_ADMIN: 'SYSTEM_ADMIN',
  INTEGRATION_ADMIN: 'INTEGRATION_ADMIN'
};

export const RESOURCE_TYPES = {
  // Core Resources
  LEGAL_ENTITY: 'LEGAL_ENTITY',
  SUPPLIER: 'SUPPLIER',
  IMPACT: 'IMPACT',
  OBLIGATION: 'OBLIGATION',
  EVIDENCE: 'EVIDENCE',
  CLAIM: 'CLAIM',
  COVERAGE: 'COVERAGE',
  AUDIT_PACK: 'AUDIT_PACK',
  SNAPSHOT: 'SNAPSHOT',
  // UI Resources
  EXPOSURE: 'EXPOSURE',
  LIABILITY: 'LIABILITY',
  MODEL: 'MODEL',
  EVENT: 'EVENT',
  EXPORT: 'EXPORT'
};

export const ACTIONS = {
  VIEW: 'VIEW',
  VIEW_DETAIL: 'VIEW_DETAIL',
  VIEW_HASH: 'VIEW_HASH',
  DRILL: 'DRILL',
  LIST: 'LIST',
  EXPORT: 'EXPORT',
  CREATE: 'CREATE',
  UPDATE: 'UPDATE',
  DELETE: 'DELETE',
  TRANSITION: 'TRANSITION',
  VALIDATE: 'VALIDATE',
  REJECT: 'REJECT',
  APPROVE: 'APPROVE',
  LOCK: 'LOCK',
  UNLOCK: 'UNLOCK',
  ESCALATE: 'ESCALATE',
  ASSIGN_OWNER: 'ASSIGN_OWNER',
  EXTEND_DEADLINE: 'EXTEND_DEADLINE',
  OVERRIDE: 'OVERRIDE',
  GENERATE: 'GENERATE',
  PUT_LEGAL_HOLD: 'PUT_LEGAL_HOLD',
  PROOF_BUNDLE: 'PROOF_BUNDLE'
};

// =============================================================================
// SUPPLIER TIER LEVELS
// =============================================================================

const TIER_ORDER = {
  'TIER_1': 1,
  'TIER_2': 2,
  'TIER_3': 3
};

function exceedsTier(contextTier, maxTier) {
  if (!contextTier || !maxTier) return false;
  return (TIER_ORDER[contextTier] || 0) > (TIER_ORDER[maxTier] || 99);
}

// =============================================================================
// ROLE HIERARCHY & TIER MAPPING
// =============================================================================

export const ROLE_TIERS = {
  BOARD_VIEWER: 0,
  EXEC_SPONSOR: 0,
  COMPLIANCE_OWNER: 1,
  COMPLIANCE_ANALYST: 1,
  LEGAL_COUNSEL: 2,
  LEGAL_ADMIN: 2,
  SUSTAINABILITY_LEAD: 3,
  SUSTAINABILITY_ANALYST: 3,
  PROCUREMENT_OWNER: 3,
  REMEDIATION_OWNER: 3,
  INTERNAL_AUDIT: 4,
  EXTERNAL_AUDITOR: 4,
  SYSTEM_ADMIN: 5,
  INTEGRATION_ADMIN: 5
};

// =============================================================================
// FULL PERMISSION MATRIX
// =============================================================================

export const PERMISSION_MATRIX = {
  // =========================================================================
  // AUDIT PACK PERMISSIONS
  // =========================================================================
  'AUDIT_PACK:VIEW': {
    allowedRoles: ['BOARD_VIEWER', 'EXEC_SPONSOR', 'COMPLIANCE_OWNER', 'COMPLIANCE_ANALYST', 
                   'LEGAL_COUNSEL', 'LEGAL_ADMIN', 'SUSTAINABILITY_LEAD', 'INTERNAL_AUDIT', 'EXTERNAL_AUDITOR']
  },
  'AUDIT_PACK:GENERATE': {
    allowedRoles: ['COMPLIANCE_OWNER', 'LEGAL_COUNSEL', 'INTERNAL_AUDIT'],
    requiresApproval: ['SUSTAINABILITY_LEAD', 'COMPLIANCE_ANALYST']
  },
  'AUDIT_PACK:EXPORT': {
    allowedRoles: ['COMPLIANCE_OWNER', 'COMPLIANCE_ANALYST', 'LEGAL_COUNSEL', 'INTERNAL_AUDIT', 'EXTERNAL_AUDITOR'],
    requiresApproval: ['SUSTAINABILITY_LEAD']
  },
  'AUDIT_PACK:LOCK': {
    allowedRoles: ['LEGAL_COUNSEL']
  },
  'AUDIT_PACK:UNLOCK': {
    allowedRoles: ['LEGAL_COUNSEL'],
    requiresDualApproval: ['LEGAL_COUNSEL', 'COMPLIANCE_OWNER']
  },

  // =========================================================================
  // SNAPSHOT PERMISSIONS
  // =========================================================================
  'SNAPSHOT:VIEW': {
    allowedRoles: ['BOARD_VIEWER', 'EXEC_SPONSOR', 'COMPLIANCE_OWNER', 'COMPLIANCE_ANALYST',
                   'LEGAL_COUNSEL', 'LEGAL_ADMIN', 'SUSTAINABILITY_LEAD', 'INTERNAL_AUDIT', 'EXTERNAL_AUDITOR']
  },
  'SNAPSHOT:LOCK': {
    allowedRoles: ['LEGAL_COUNSEL']
  },
  'SNAPSHOT:UNLOCK': {
    allowedRoles: ['LEGAL_COUNSEL'],
    requiresDualApproval: ['LEGAL_COUNSEL', 'COMPLIANCE_OWNER']
  },

  // =========================================================================
  // EXPOSURE (KPI) PERMISSIONS
  // =========================================================================
  'EXPOSURE:VIEW': {
    allowedRoles: ['BOARD_VIEWER', 'EXEC_SPONSOR', 'COMPLIANCE_OWNER', 'COMPLIANCE_ANALYST',
                   'LEGAL_COUNSEL', 'SUSTAINABILITY_LEAD', 'SUSTAINABILITY_ANALYST', 
                   'INTERNAL_AUDIT', 'EXTERNAL_AUDITOR']
  },
  'EXPOSURE:DRILL': {
    allowedRoles: ['COMPLIANCE_OWNER', 'COMPLIANCE_ANALYST', 'LEGAL_COUNSEL', 
                   'SUSTAINABILITY_LEAD', 'INTERNAL_AUDIT', 'EXTERNAL_AUDITOR'],
    deniedRoles: ['BOARD_VIEWER', 'EXEC_SPONSOR']
  },

  // =========================================================================
  // LIABILITY PERMISSIONS
  // =========================================================================
  'LIABILITY:VIEW': {
    allowedRoles: ['BOARD_VIEWER', 'EXEC_SPONSOR', 'COMPLIANCE_OWNER', 'COMPLIANCE_ANALYST',
                   'LEGAL_COUNSEL', 'SUSTAINABILITY_LEAD', 'INTERNAL_AUDIT', 'EXTERNAL_AUDITOR']
  },
  'LIABILITY:DRILL': {
    allowedRoles: ['COMPLIANCE_OWNER', 'COMPLIANCE_ANALYST', 'LEGAL_COUNSEL', 
                   'SUSTAINABILITY_LEAD', 'INTERNAL_AUDIT', 'EXTERNAL_AUDITOR'],
    deniedRoles: ['BOARD_VIEWER', 'EXEC_SPONSOR']
  },

  // =========================================================================
  // OBLIGATION PERMISSIONS
  // =========================================================================
  'OBLIGATION:VIEW': {
    allowedRoles: ['BOARD_VIEWER', 'EXEC_SPONSOR', 'COMPLIANCE_OWNER', 'COMPLIANCE_ANALYST',
                   'LEGAL_COUNSEL', 'SUSTAINABILITY_LEAD', 'SUSTAINABILITY_ANALYST',
                   'REMEDIATION_OWNER', 'INTERNAL_AUDIT', 'EXTERNAL_AUDITOR']
  },
  'OBLIGATION:TRANSITION': {
    allowedRoles: ['COMPLIANCE_OWNER', 'REMEDIATION_OWNER'],
    requiresApproval: ['COMPLIANCE_ANALYST', 'SUSTAINABILITY_LEAD', 'LEGAL_COUNSEL'],
    limitedRoles: {
      'REMEDIATION_OWNER': ['IN_PROGRESS', 'COMPLETE', 'BLOCKED'] // Can only transition these states
    }
  },
  'OBLIGATION:ASSIGN_OWNER': {
    allowedRoles: ['COMPLIANCE_OWNER', 'LEGAL_COUNSEL'],
    requiresApproval: ['COMPLIANCE_ANALYST', 'SUSTAINABILITY_LEAD']
  },
  'OBLIGATION:EXTEND_DEADLINE': {
    allowedRoles: ['LEGAL_COUNSEL'],
    requiresApproval: ['COMPLIANCE_OWNER']
  },
  'OBLIGATION:OVERRIDE': {
    allowedRoles: ['LEGAL_COUNSEL']
  },

  // =========================================================================
  // EVIDENCE PERMISSIONS
  // =========================================================================
  'EVIDENCE:VIEW': {
    allowedRoles: ['COMPLIANCE_OWNER', 'COMPLIANCE_ANALYST', 'LEGAL_COUNSEL', 
                   'SUSTAINABILITY_LEAD', 'SUSTAINABILITY_ANALYST', 'INTERNAL_AUDIT', 'EXTERNAL_AUDITOR']
  },
  'EVIDENCE:VIEW_HASH': {
    allowedRoles: ['COMPLIANCE_OWNER', 'LEGAL_COUNSEL', 'INTERNAL_AUDIT'],
    deniedRoles: ['BOARD_VIEWER', 'EXEC_SPONSOR', 'SUSTAINABILITY_LEAD', 'SUSTAINABILITY_ANALYST']
  },
  'EVIDENCE:UPLOAD': {
    allowedRoles: ['COMPLIANCE_OWNER', 'COMPLIANCE_ANALYST', 'LEGAL_COUNSEL',
                   'SUSTAINABILITY_LEAD', 'SUSTAINABILITY_ANALYST', 'INTERNAL_AUDIT', 'EXTERNAL_AUDITOR']
  },
  'EVIDENCE:VALIDATE': {
    allowedRoles: ['COMPLIANCE_ANALYST', 'COMPLIANCE_OWNER', 'LEGAL_COUNSEL', 'INTERNAL_AUDIT', 'EXTERNAL_AUDITOR'],
    sodRules: ['cannotValidateEvidenceUploadedBySelf']
  },
  'EVIDENCE:REJECT': {
    allowedRoles: ['COMPLIANCE_ANALYST', 'COMPLIANCE_OWNER', 'LEGAL_COUNSEL', 'INTERNAL_AUDIT', 'EXTERNAL_AUDITOR'],
    sodRules: ['cannotValidateEvidenceUploadedBySelf']
  },
  'EVIDENCE:EXPORT': {
    allowedRoles: ['COMPLIANCE_OWNER', 'COMPLIANCE_ANALYST', 'LEGAL_COUNSEL', 'INTERNAL_AUDIT', 'EXTERNAL_AUDITOR'],
    requiresApproval: ['SUSTAINABILITY_ANALYST']
  },
  'EVIDENCE:PUT_LEGAL_HOLD': {
    allowedRoles: ['LEGAL_COUNSEL']
  },

  // =========================================================================
  // IMPACT PERMISSIONS
  // =========================================================================
  'IMPACT:VIEW': {
    allowedRoles: ['BOARD_VIEWER', 'EXEC_SPONSOR', 'COMPLIANCE_OWNER', 'COMPLIANCE_ANALYST',
                   'LEGAL_COUNSEL', 'SUSTAINABILITY_LEAD', 'SUSTAINABILITY_ANALYST',
                   'PROCUREMENT_OWNER', 'INTERNAL_AUDIT', 'EXTERNAL_AUDITOR']
  },
  'IMPACT:CREATE': {
    allowedRoles: ['SUSTAINABILITY_LEAD', 'SUSTAINABILITY_ANALYST', 'COMPLIANCE_OWNER',
                   'COMPLIANCE_ANALYST', 'LEGAL_COUNSEL', 'PROCUREMENT_OWNER']
  },
  'IMPACT:ESCALATE': {
    allowedRoles: ['LEGAL_COUNSEL'],
    requiresApproval: ['SUSTAINABILITY_LEAD', 'COMPLIANCE_OWNER']
  },
  'IMPACT:OVERRIDE': {
    allowedRoles: ['LEGAL_COUNSEL'],
    requiresApproval: ['COMPLIANCE_OWNER']
  },

  // =========================================================================
  // CLAIM PERMISSIONS
  // =========================================================================
  'CLAIM:VIEW': {
    allowedRoles: ['BOARD_VIEWER', 'EXEC_SPONSOR', 'COMPLIANCE_OWNER', 'COMPLIANCE_ANALYST',
                   'LEGAL_COUNSEL', 'SUSTAINABILITY_LEAD', 'INTERNAL_AUDIT', 'EXTERNAL_AUDITOR']
  },
  'CLAIM:UPDATE': {
    allowedRoles: ['LEGAL_COUNSEL'],
    requiresApproval: ['COMPLIANCE_OWNER']
  },
  'CLAIM:LOCK': {
    allowedRoles: ['LEGAL_COUNSEL'],
    requiresApproval: ['COMPLIANCE_OWNER']
  },
  'CLAIM:EXPORT': {
    allowedRoles: ['COMPLIANCE_OWNER', 'LEGAL_COUNSEL', 'INTERNAL_AUDIT'],
    requiresApproval: ['SUSTAINABILITY_LEAD']
  },

  // =========================================================================
  // COVERAGE PERMISSIONS
  // =========================================================================
  'COVERAGE:VIEW': {
    allowedRoles: ['COMPLIANCE_OWNER', 'COMPLIANCE_ANALYST', 'LEGAL_COUNSEL',
                   'SUSTAINABILITY_LEAD', 'INTERNAL_AUDIT', 'EXTERNAL_AUDITOR']
  },

  // =========================================================================
  // MODEL PERMISSIONS (Computation Metadata)
  // =========================================================================
  'MODEL:VIEW': {
    allowedRoles: ['COMPLIANCE_OWNER', 'COMPLIANCE_ANALYST', 'LEGAL_COUNSEL', 
                   'INTERNAL_AUDIT', 'EXTERNAL_AUDITOR', 'SYSTEM_ADMIN']
  },

  // =========================================================================
  // EVENT PERMISSIONS
  // =========================================================================
  'EVENT:VIEW': {
    allowedRoles: ['COMPLIANCE_OWNER', 'COMPLIANCE_ANALYST', 'LEGAL_COUNSEL', 
                   'INTERNAL_AUDIT', 'EXTERNAL_AUDITOR', 'SYSTEM_ADMIN']
  },

  // =========================================================================
  // EXPORT PERMISSIONS
  // =========================================================================
  'EXPORT:PROOF_BUNDLE': {
    allowedRoles: ['COMPLIANCE_OWNER', 'COMPLIANCE_ANALYST', 'LEGAL_COUNSEL', 'INTERNAL_AUDIT', 'EXTERNAL_AUDITOR'],
    requiresApproval: ['SUSTAINABILITY_LEAD']
  },

  // =========================================================================
  // SUPPLIER PERMISSIONS
  // =========================================================================
  'SUPPLIER:VIEW': {
    allowedRoles: ['COMPLIANCE_OWNER', 'COMPLIANCE_ANALYST', 'LEGAL_COUNSEL',
                   'SUSTAINABILITY_LEAD', 'SUSTAINABILITY_ANALYST', 'PROCUREMENT_OWNER',
                   'INTERNAL_AUDIT', 'EXTERNAL_AUDITOR']
  },
  'SUPPLIER:CREATE': {
    allowedRoles: ['PROCUREMENT_OWNER', 'COMPLIANCE_OWNER', 'SUSTAINABILITY_LEAD']
  },

  // =========================================================================
  // LEGAL ENTITY PERMISSIONS
  // =========================================================================
  'LEGAL_ENTITY:VIEW': {
    allowedRoles: ['BOARD_VIEWER', 'EXEC_SPONSOR', 'COMPLIANCE_OWNER', 'COMPLIANCE_ANALYST',
                   'LEGAL_COUNSEL', 'LEGAL_ADMIN', 'SUSTAINABILITY_LEAD', 'INTERNAL_AUDIT', 'EXTERNAL_AUDITOR']
  },
  'LEGAL_ENTITY:CREATE': {
    allowedRoles: ['COMPLIANCE_OWNER', 'LEGAL_ADMIN', 'SYSTEM_ADMIN']
  }
};

// =============================================================================
// SESSION STATE FUNCTIONS
// =============================================================================

/**
 * Set current session capabilities (called after login)
 */
export function setSessionCapabilities(session) {
  currentSession = session;
}

/**
 * Get current session
 */
export function getSession() {
  return currentSession;
}

/**
 * Clear session (on logout)
 */
export function clearSession() {
  currentSession = null;
}

/**
 * Set snapshot lock state
 */
export function setSnapshotLockState(lockState) {
  snapshotLockState = lockState;
}

/**
 * Get snapshot lock state
 */
export function getSnapshotLockState() {
  return snapshotLockState;
}

// =============================================================================
// CORE PERMISSION ENGINE - The can() Function
// =============================================================================

/**
 * Check if user can perform action on resource
 * 
 * @param {string} action - The action to perform
 * @param {string} resource - The resource type
 * @param {Object} context - Additional context for ABAC/SoD checks
 * @returns {Object} Permission result with allowed, reason, etc.
 */
export function can(action, resource, context = {}) {
  if (!currentSession) {
    return { allowed: false, reason: 'No active session' };
  }

  const permissionKey = `${resource}:${action}`;
  const matrixEntry = PERMISSION_MATRIX[permissionKey];

  // =========================================================================
  // 1️⃣ SNAPSHOT LOCK CHECK - If locked, disable state-changing actions
  // =========================================================================
  if (snapshotLockState.isLocked) {
    const lockedActions = ['TRANSITION', 'VALIDATE', 'REJECT', 'ASSIGN_OWNER', 'UPDATE', 'CREATE', 'DELETE'];
    if (lockedActions.includes(action)) {
      return { 
        allowed: false, 
        reason: `Snapshot is locked (${snapshotLockState.snapshotId}). Only viewing and exporting allowed.`,
        isSnapshotLocked: true
      };
    }
  }

  // =========================================================================
  // 2️⃣ EXPLICIT PERMISSION CHECK (Direct Grant)
  // =========================================================================
  if (currentSession.permissions && currentSession.permissions.includes(permissionKey)) {
    // Still need to check SoD rules
    const sodResult = checkSoDRules(matrixEntry?.sodRules, context);
    if (!sodResult.allowed) {
      return sodResult;
    }
    // Check ABAC
    const abacResult = checkABACConstraints(context);
    if (!abacResult.allowed) {
      return abacResult;
    }
    return { allowed: true };
  }

  // =========================================================================
  // 3️⃣ PERMISSION MATRIX LOOKUP
  // =========================================================================
  if (!matrixEntry) {
    return { allowed: false, reason: `No permission defined for ${permissionKey}` };
  }

  // =========================================================================
  // 4️⃣ DENIED ROLES CHECK
  // =========================================================================
  if (matrixEntry.deniedRoles) {
    const hasDeniedRole = currentSession.roles.some(r => matrixEntry.deniedRoles.includes(r));
    if (hasDeniedRole) {
      return { allowed: false, reason: 'Action denied for your role' };
    }
  }

  // =========================================================================
  // 5️⃣ ALLOWED ROLES CHECK (RBAC)
  // =========================================================================
  const hasAllowedRole = currentSession.roles.some(r => matrixEntry.allowedRoles.includes(r));
  
  if (!hasAllowedRole) {
    // Check if approval path exists
    if (matrixEntry.requiresApproval) {
      const needsApproval = currentSession.roles.some(r => matrixEntry.requiresApproval.includes(r));
      if (needsApproval) {
        const approvalRole = matrixEntry.allowedRoles[0];
        return {
          allowed: false,
          reason: `Requires ${formatRoleName(approvalRole)} approval`,
          requiresApproval: true,
          approvalRole,
          escalatePath: `/approvals/request?action=${permissionKey}`
        };
      }
    }
    
    // Check dual approval requirement
    if (matrixEntry.requiresDualApproval) {
      return {
        allowed: false,
        reason: `Requires dual approval: ${matrixEntry.requiresDualApproval.map(formatRoleName).join(' + ')}`,
        requiresDualApproval: true,
        requiredRoles: matrixEntry.requiresDualApproval
      };
    }
    
    return { 
      allowed: false, 
      reason: `Requires ${matrixEntry.allowedRoles.map(formatRoleName).join(' or ')} role` 
    };
  }

  // =========================================================================
  // 6️⃣ ABAC CHECKS (Scope Constraints)
  // =========================================================================
  const abacResult = checkABACConstraints(context);
  if (!abacResult.allowed) {
    return abacResult;
  }

  // =========================================================================
  // 7️⃣ SOD CHECKS (Separation of Duties)
  // =========================================================================
  const sodResult = checkSoDRules(matrixEntry.sodRules, context);
  if (!sodResult.allowed) {
    return sodResult;
  }

  // =========================================================================
  // 8️⃣ LIMITED ROLE ACTIONS CHECK
  // =========================================================================
  if (matrixEntry.limitedRoles && context.targetState) {
    for (const role of currentSession.roles) {
      if (matrixEntry.limitedRoles[role]) {
        const allowedStates = matrixEntry.limitedRoles[role];
        if (!allowedStates.includes(context.targetState)) {
          return {
            allowed: false,
            reason: `${formatRoleName(role)} can only transition to: ${allowedStates.join(', ')}`
          };
        }
      }
    }
  }

  return { allowed: true };
}

// =============================================================================
// ABAC (Attribute-Based Access Control) Checks
// =============================================================================

function checkABACConstraints(context) {
  if (!currentSession || !context) {
    return { allowed: true };
  }

  // Entity scope check
  if (context.legalEntityId && 
      currentSession.scope?.legalEntityIds?.length > 0 &&
      !currentSession.scope.legalEntityIds.includes(context.legalEntityId)) {
    return {
      allowed: false,
      reason: 'Resource is outside your authorized legal entities'
    };
  }

  // Boundary scope check
  if (context.boundaryId && 
      currentSession.scope?.boundaryIds?.length > 0 &&
      !currentSession.scope.boundaryIds.includes(context.boundaryId)) {
    return {
      allowed: false,
      reason: 'Resource is outside your authorized reporting boundaries'
    };
  }

  // Supplier tier restriction
  if (context.supplierTier && 
      currentSession.scope?.supplierTierMax &&
      exceedsTier(context.supplierTier, currentSession.scope.supplierTierMax)) {
    return {
      allowed: false,
      reason: `Your access is limited to ${currentSession.scope.supplierTierMax} suppliers`
    };
  }

  // Confidentiality level check
  if (context.confidentialityLevel !== undefined && 
      currentSession.scope?.maxConfidentiality !== undefined &&
      context.confidentialityLevel > currentSession.scope.maxConfidentiality) {
    return {
      allowed: false,
      reason: 'Insufficient clearance for this confidentiality level'
    };
  }

  return { allowed: true };
}

// =============================================================================
// SoD (Separation of Duties) Checks
// =============================================================================

function checkSoDRules(rules, context) {
  if (!rules || rules.length === 0 || !currentSession || !context) {
    return { allowed: true };
  }

  for (const rule of rules) {
    // Evidence uploader cannot validate their own upload
    if (rule === 'cannotValidateEvidenceUploadedBySelf') {
      if (currentSession.sod?.cannotValidateEvidenceUploadedBySelf !== false) {
        if (context.createdBy === currentSession.userId || context.uploaderId === currentSession.userId) {
          return {
            allowed: false,
            reason: 'Separation of duties: you cannot validate evidence you uploaded',
            sodViolation: true
          };
        }
      }
    }

    // Cannot approve own work
    if (rule === 'cannotApproveOwnWork') {
      if (currentSession.sod?.cannotApproveOwnWork !== false) {
        if (context.createdBy === currentSession.userId || context.ownerId === currentSession.userId) {
          return {
            allowed: false,
            reason: 'Separation of duties: you cannot approve your own work',
            sodViolation: true
          };
        }
      }
    }
  }

  return { allowed: true };
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function formatRoleName(role) {
  return role.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Check if field should be masked for current user
 */
export function shouldMask(fieldType, context = {}) {
  if (!currentSession) return true;

  switch (fieldType) {
    case 'EVIDENCE_HASH':
      return !can('VIEW_HASH', 'EVIDENCE', context).allowed;
    
    case 'CONTRIBUTOR_IDS':
      // Board viewers see summary counts only
      if (currentSession.roles.some(r => ['BOARD_VIEWER', 'EXEC_SPONSOR'].includes(r))) {
        return true;
      }
      return false;
    
    case 'CASE_DETAILS':
      // Check confidentiality
      if (context.confidentialityLevel !== undefined && 
          currentSession.scope?.maxConfidentiality !== undefined &&
          context.confidentialityLevel > currentSession.scope.maxConfidentiality) {
        return true;
      }
      return false;
    
    case 'DELTA_DRIVERS':
      return !can('VIEW', 'EVENT', context).allowed;
    
    default:
      return false;
  }
}

/**
 * Get visible actions for a resource
 */
export function getVisibleActions(resource) {
  if (!currentSession) return [];

  const visibleActions = [];
  const allActions = Object.values(ACTIONS);

  allActions.forEach(action => {
    const result = can(action, resource);
    if (result.allowed || result.requiresApproval) {
      visibleActions.push({
        action,
        allowed: result.allowed,
        requiresApproval: result.requiresApproval,
        reason: result.reason
      });
    }
  });

  return visibleActions;
}

/**
 * Get user's primary (highest-privilege) role
 */
export function getPrimaryRole() {
  if (!currentSession || !currentSession.roles || currentSession.roles.length === 0) return null;
  
  return currentSession.roles.reduce((highest, current) => {
    return (ROLE_TIERS[current] ?? 99) < (ROLE_TIERS[highest] ?? 99) ? current : highest;
  });
}

/**
 * Check if user has specific role
 */
export function hasRole(roles) {
  if (!currentSession) return false;
  const roleArray = Array.isArray(roles) ? roles : [roles];
  return currentSession.roles.some(r => roleArray.includes(r));
}

/**
 * Check if user is in board view (read-only leadership)
 */
export function isBoardView() {
  return hasRole(['BOARD_VIEWER', 'EXEC_SPONSOR']);
}

/**
 * Check if user is legal
 */
export function isLegal() {
  return hasRole(['LEGAL_COUNSEL', 'LEGAL_ADMIN']);
}

/**
 * Check if user is compliance
 */
export function isCompliance() {
  return hasRole(['COMPLIANCE_OWNER', 'COMPLIANCE_ANALYST']);
}

/**
 * Check permission for UI components (returns UI hints)
 */
export function checkPermission(action, resource, context = {}) {
  const result = can(action, resource, context);

  return {
    allowed: result.allowed,
    reason: result.reason,
    showDisabled: !result.allowed && !result.requiresApproval,
    showApprovalPath: result.requiresApproval || false,
    showDualApproval: result.requiresDualApproval || false,
    tooltip: result.reason,
    approvalRole: result.approvalRole,
    isSnapshotLocked: result.isSnapshotLocked || false,
    sodViolation: result.sodViolation || false
  };
}

/**
 * Get role display info
 */
export function getRoleDisplayInfo(role) {
  const ROLE_DISPLAY = {
    BOARD_VIEWER: { label: 'Board Viewer', tier: 'Leadership', color: 'purple' },
    EXEC_SPONSOR: { label: 'Executive Sponsor', tier: 'Leadership', color: 'purple' },
    COMPLIANCE_OWNER: { label: 'Compliance Owner', tier: 'Compliance', color: 'emerald' },
    COMPLIANCE_ANALYST: { label: 'Compliance Analyst', tier: 'Compliance', color: 'emerald' },
    LEGAL_COUNSEL: { label: 'Legal Counsel', tier: 'Legal', color: 'blue' },
    LEGAL_ADMIN: { label: 'Legal Admin', tier: 'Legal', color: 'blue' },
    SUSTAINABILITY_LEAD: { label: 'Sustainability Lead', tier: 'Operations', color: 'amber' },
    SUSTAINABILITY_ANALYST: { label: 'Sustainability Analyst', tier: 'Operations', color: 'amber' },
    PROCUREMENT_OWNER: { label: 'Procurement Owner', tier: 'Operations', color: 'amber' },
    REMEDIATION_OWNER: { label: 'Remediation Owner', tier: 'Operations', color: 'amber' },
    INTERNAL_AUDIT: { label: 'Internal Audit', tier: 'Assurance', color: 'cyan' },
    EXTERNAL_AUDITOR: { label: 'External Auditor', tier: 'Assurance', color: 'cyan' },
    SYSTEM_ADMIN: { label: 'System Admin', tier: 'Platform', color: 'slate' },
    INTEGRATION_ADMIN: { label: 'Integration Admin', tier: 'Platform', color: 'slate' }
  };

  return ROLE_DISPLAY[role] || { label: role, tier: 'Unknown', color: 'gray' };
}

/**
 * Require utility - returns component or null
 */
export function require(action, resource, context = {}) {
  return can(action, resource, context).allowed;
}

export default {
  can,
  require,
  shouldMask,
  getVisibleActions,
  getPrimaryRole,
  hasRole,
  isBoardView,
  isLegal,
  isCompliance,
  setSessionCapabilities,
  getSession,
  clearSession,
  setSnapshotLockState,
  getSnapshotLockState,
  checkPermission,
  getRoleDisplayInfo,
  ROLES,
  ACTIONS,
  RESOURCE_TYPES,
  ROLE_TIERS,
  PERMISSION_MATRIX
};
